import * as elog from '.';
import { getDwellSessionID, setDwellSessionID, getSession } from './analytics-cookie';

/*
 * Fires one 'session_dwell' event per session, once a page has been visible
 * for DWELL_SECONDS. Its presence tells the warehouse the session passed the
 * GA4 10-second engagement test; its absence means no page was visible that
 * long, or JS never ran.
 *
 * The event goes to the event API via elog, not mParticle, and elog attaches
 * user_context so it carries our session id and joins to the session's page
 * views.
 *
 * - The countdown restarts on every page view and pauses while the tab is
 *   hidden, so the event means 10 visible seconds on one page.
 * - The elog-dwell cookie records the session that already fired, so a
 *   session fires at most once. See fire() for the checks.
 */

export const SESSION_DWELL_EVENT_NAME = 'session_dwell';
export const DWELL_SECONDS = 10;

let armedSessionID: string | null = null;
let visibleMs = 0;
let visibleSince: number | null = null;
let timeoutId: ReturnType<typeof setTimeout> | null = null;

function fire() {
  const sessionID = armedSessionID;
  timeoutId = null;
  disarm();

  if (!sessionID) { return; }

  // Another tab may have crossed the threshold for this session first.
  if (getDwellSessionID() === sessionID) { return; }

  // Or the session rotated while this page sat hidden.
  const currentSessionID = getSession().sessionID;
  if (currentSessionID && currentSessionID !== sessionID) { return; }

  setDwellSessionID(sessionID);
  elog.info(SESSION_DWELL_EVENT_NAME);
}

function resume() {
  visibleSince = performance.now();
  timeoutId = setTimeout(fire, (DWELL_SECONDS * 1000) - visibleMs);
}

function pause() {
  if (timeoutId !== null) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }

  if (visibleSince !== null) {
    visibleMs += performance.now() - visibleSince;
    visibleSince = null;
  }
}

function onVisibilityChange() {
  if (document.visibilityState === 'visible') {
    resume();
  } else {
    pause();
  }
}

export function disarm() {
  pause();
  visibleMs = 0;
  armedSessionID = null;
  document.removeEventListener('visibilitychange', onVisibilityChange);
}

export function trackDwell(sessionID: string) {
  if (typeof document === 'undefined') { return; }

  disarm();
  if (getDwellSessionID() === sessionID) { return; }

  armedSessionID = sessionID;
  document.addEventListener('visibilitychange', onVisibilityChange);
  if (document.visibilityState === 'visible') { resume(); }
}
